import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, fmtDT, fmtQty, todayISO } from "../../api";
import { useAuth } from "../../context/AuthContext";
import StatusBadge from "../../components/StatusBadge";
import MfgTabs from "../../components/MfgTabs";
import BoxesPanel from "../../components/BoxesPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { QrCode, Play, CheckCircle, XCircle, PlusCircle } from "lucide-react";

function EntryDialog({ open, onClose, mo, onSaved }) {
  const [f, setF] = useState({ qty: "", date: todayISO(), start_time: "", end_time: "", operator: "", remarks: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ qty: "", date: todayISO(), start_time: "", end_time: "", operator: "", remarks: "" }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/mos/${mo.id}/production-entries`, { ...f, qty: parseFloat(f.qty) });
      toast.success("Production recorded — raw materials consumed, finished goods updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="production-entry-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[#002B5D]">Record Production</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Remaining: <span className="font-mono font-semibold">{fmtQty(mo.remaining_qty)} {mo.uom}</span></p>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="sk-label">Quantity *</label>
              <input data-testid="entry-qty-input" type="number" step="any" min="0.001" max={mo.remaining_qty} required className="sk-input font-mono" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} />
            </div>
            <div>
              <label className="sk-label">Date</label>
              <input data-testid="entry-date-input" type="date" className="sk-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
            </div>
            <div>
              <label className="sk-label">Start Time</label>
              <input data-testid="entry-start-input" type="time" className="sk-input" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} />
            </div>
            <div>
              <label className="sk-label">End Time</label>
              <input data-testid="entry-end-input" type="time" className="sk-input" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="sk-label">Operator</label>
            <input data-testid="entry-operator-input" className="sk-input" value={f.operator} onChange={(e) => setF({ ...f, operator: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Remarks</label>
            <input data-testid="entry-remarks-input" className="sk-input" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </div>
          <button data-testid="entry-save-button" disabled={busy} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#EB1C27" }}>
            {busy ? "Recording..." : "Record Production"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function MODetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [mo, setMo] = useState(null);
  const [entryOpen, setEntryOpen] = useState(false);
  const navigate = useNavigate();
  const canOperate = ["admin", "production"].includes(user?.role);

  const genLot = async () => {
    try {
      const r = await api.post(`/mos/${id}/generate-lot`, {});
      toast.success(`${r.data.lot_number} generated`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const load = useCallback(() => {
    api.get(`/mos/${id}`).then((r) => setMo(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!mo) return <div className="text-slate-500" data-testid="mo-detail-loading">Loading...</div>;

  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const complete = () => act(async () => {
    try {
      await api.post(`/mos/${id}/complete`, {});
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 400 && typeof d === "string" && d.includes("Confirm closing")) {
        if (window.confirm(`${d}\n\nClose the remaining quantity and complete this order?`)) {
          await api.post(`/mos/${id}/complete`, { confirm_close: true });
          return;
        }
      }
      throw e;
    }
  }, "MO completed");

  const cancel = () => {
    if (!window.confirm("Cancel this manufacturing order? Recorded production entries stay in history.")) return;
    act(() => api.patch(`/mos/${id}/status`, { status: "Cancelled" }), "MO cancelled");
  };

  const pct = mo.planned_qty > 0 ? Math.min(100, (mo.produced_qty / mo.planned_qty) * 100) : 0;
  const useBoxes = (mo.boxes?.length || 0) > 0 || mo.production_entries.length === 0;

  return (
    <div data-testid="mo-detail-page" className="space-y-6">
      <MfgTabs />
      <div className="flex flex-wrap items-start justify-between gap-4 mt-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] font-mono tracking-tight" data-testid="mo-number">{mo.mo_number}</h1>
            <StatusBadge status={mo.status} testid="mo-detail-status" />
          </div>
          <p className="text-slate-500 mt-1">{mo.product_name} <span className="font-mono text-xs">({mo.product_code})</span></p>
        </div>
        {canOperate && (
          <div className="flex flex-wrap gap-2">
            {mo.status === "Draft" && (
              <button data-testid="mo-ready-button" onClick={() => act(() => api.patch(`/mos/${id}/status`, { status: "Ready" }), "MO marked Ready")}
                className="h-11 px-5 rounded-md font-heading font-bold text-sm bg-sky-600 text-white">Mark Ready</button>
            )}
            {!mo.lot_id && mo.status !== "Cancelled" && (
              <button data-testid="generate-lot-button" onClick={genLot}
                className="h-11 px-5 rounded-md font-heading font-bold text-sm text-white inline-flex items-center gap-2" style={{ backgroundColor: "#EB1C27" }}>
                <QrCode className="w-4 h-4" /> GENERATE LOT
              </button>
            )}
            {mo.status === "Ready" && !useBoxes && (
              <button data-testid="mo-start-button" onClick={() => act(() => api.patch(`/mos/${id}/status`, { status: "In Progress" }), "Production started")}
                className="h-11 px-5 rounded-md font-heading font-bold text-sm bg-amber-500 text-white inline-flex items-center gap-2">
                <Play className="w-4 h-4" /> START PRODUCTION
              </button>
            )}
            {mo.status === "In Progress" && (
              <>
                {!useBoxes && (
                <button data-testid="mo-record-production-button" onClick={() => setEntryOpen(true)}
                  className="h-11 px-5 rounded-md font-heading font-bold text-sm text-white inline-flex items-center gap-2" style={{ backgroundColor: "#002B5D" }}>
                  <PlusCircle className="w-4 h-4" /> Record Production
                </button>
                )}
                <button data-testid="mo-complete-button" onClick={complete}
                  className="h-11 px-5 rounded-md font-heading font-bold text-sm bg-green-600 text-white inline-flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> COMPLETE PRODUCTION
                </button>
              </>
            )}
            {["Draft", "Ready", "In Progress"].includes(mo.status) && (
              <button data-testid="mo-cancel-button" onClick={cancel}
                className="h-11 px-4 rounded-md font-heading font-bold text-sm border border-rose-300 text-rose-600 inline-flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Cancel
              </button>
            )}
            <Link to="/manufacturing/orders/new" data-testid="mo-new-order-button"
              className="h-11 px-5 rounded-md font-heading font-bold text-sm border border-[#002B5D] text-[#002B5D] inline-flex items-center gap-2">
              <PlusCircle className="w-4 h-4" /> New Order
            </Link>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="sk-card p-5 space-y-3" data-testid="mo-summary">
          <h3 className="font-heading font-bold text-[#002B5D]">Summary</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-slate-500">Planned</dt><dd className="font-mono font-semibold" data-testid="mo-planned">{fmtQty(mo.planned_qty)} {mo.uom}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Produced</dt><dd className="font-mono font-semibold text-green-700" data-testid="mo-produced">{fmtQty(mo.produced_qty)} {mo.uom}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Remaining</dt><dd className="font-mono font-semibold text-amber-700" data-testid="mo-remaining">{fmtQty(mo.remaining_qty)} {mo.uom}</dd></div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: "#EB1C27" }} /></div>
            <div className="flex justify-between"><dt className="text-slate-500">BOM</dt><dd>{mo.bom_name} v{mo.bom_version}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Production Rate</dt><dd className="font-mono">{mo.production_rate ? `${fmtQty(mo.production_rate)} ${mo.uom}/h` : "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Estimated Time</dt><dd className="font-mono" data-testid="mo-est-hours">{mo.estimated_hours != null ? `${mo.estimated_hours} h` : "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Started</dt><dd className="font-mono text-xs" data-testid="mo-started-at">{fmtDT(mo.started_at)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Completed</dt><dd className="font-mono text-xs" data-testid="mo-completed-at">{fmtDT(mo.completed_at)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Lot</dt><dd data-testid="mo-lot-link">{mo.lot_id ? <Link className="font-mono font-semibold text-[#EB1C27] hover:underline" to={`/lots/${mo.lot_id}`}>{mo.lot_number}</Link> : "-"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Created by</dt><dd className="text-xs">{mo.created_by}</dd></div>
          </dl>
        </div>

        <div className="sk-card md:col-span-2" data-testid="mo-requirements">
          <h3 className="font-heading font-bold text-[#002B5D] px-5 py-4 border-b border-border">Raw Material Requirements (full planned qty)</h3>
          <table className="w-full sk-table">
            <thead><tr><th>Material</th><th>Code</th><th className="text-right">Required</th><th>UOM</th></tr></thead>
            <tbody>
              {mo.requirements.map((r) => (
                <tr key={r.product_id}>
                  <td>{r.product_name}</td>
                  <td className="font-mono text-xs text-slate-500">{r.product_code || ""}</td>
                  <td className="text-right font-mono font-semibold text-[#002B5D]">{fmtQty(r.qty)}</td>
                  <td>{r.uom}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <BoxesPanel mo={mo} onChanged={load} />

      {mo.production_entries.length > 0 && (
      <div className="sk-card" data-testid="mo-entries">
        <h3 className="font-heading font-bold text-[#002B5D] px-5 py-4 border-b border-border">Production Entries</h3>
        <table className="w-full sk-table">
          <thead><tr><th>Date</th><th className="text-right">Qty</th><th>Start</th><th>End</th><th>Operator</th><th>Remarks</th></tr></thead>
          <tbody>
            {mo.production_entries.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400">No production recorded yet</td></tr>}
            {mo.production_entries.map((e) => (
              <tr key={e.id} data-testid={`entry-row-${e.id}`}>
                <td>{fmtDate(e.date)}</td>
                <td className="text-right font-mono font-semibold">{fmtQty(e.qty)} {mo.uom}</td>
                <td className="font-mono text-xs">{e.start_time || "-"}</td>
                <td className="font-mono text-xs">{e.end_time || "-"}</td>
                <td>{e.operator || "-"}</td>
                <td className="text-slate-500 text-xs">{e.remarks || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <EntryDialog open={entryOpen} onClose={() => setEntryOpen(false)} mo={mo} onSaved={load} />
    </div>
  );
}
